import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import csillaImage from '../assets/csilla.jpg';

type Testimonial = {
  name: string;
  role: string;
  content: string;
  image?: string;
};

const testimonials: Testimonial[] = [
  {
    name: "Csilla",
    role: "Ügyfél",
    image: csillaImage,
    content: `Az egyéni, 6 alkalmas online YOGIVITY program egy valódi fordulópontot jelentett számomra. A program során személyre szabott figyelmet kaptam, ami lehetővé tette, hogy teljes mértékben a saját igényeimre és céljaimra fókuszáljunk. Két nagy ajándéka a számomra:

1. Be tudtam vezetni a napi rutinomba a 15 perces rendszeres mozgást. Kezdetben nem gondoltam, hogy ennyi is elég lehet, de már néhány alkalom után érezhetővé vált a hatása. Pozitívabb, energikusabb lettem.

2. Véget értek a falási rohamaim. Ez óriási megkönnyebbülést jelentett. Már nem érzem, hogy az étkezés feszültséglevezetés vagy pótcselekvés lenne. Sokkal tudatosabbá váltam és végre békésebb a viszonyom az étellel = belső egyensúly.

Ez a 6 alkalmas folyamat sokkal többről szólt, mint mozgásról vagy étrendről — egy szemléletformáló élmény volt. Az alkalmak rugalmasan igazodtak az aktuális állapotomhoz. Valóban rólam szóltak.
Sokkal tudatosabban figyelek a testem jelzéseire, energikusabb vagyok és közelebb kerültem ahhoz az életmódhoz, amit régóta kerestem.

Ajánlom mindenkinek, aki szeretne egy biztonságos, figyelmes és személyre szabott programot a testi-lelki egészsége megőrzésére.`
  },
  {
    name: "Anna",
    role: "Yogivity résztvevő",
    content: `A Yogivity program megváltoztatta az étkezéshez való viszonyomat! Sportos, aktív életet élek és sokkal több energiát használok fel az átlagnál. Ezzel a programmal és Dorottya hasznos és logikus tanácsai hatására el tudtam fogadni, hogy a testemnek, ahhoz hogy jól működjön, nagyobb odafigyelésre van szüksége az étkezés tekintetében is! Tévhitem, hogy a több (és minőségibb!!!) étel nem egyenlő a „túlevéssel", megdőlt! A megfelelő mennyiségű és minőségű táplálék nem ellenség, hanem támogatás. Örülök, hogy ezt a felismerést átvehettem ennek a programnak köszönhetően! Gyakorlatilag életmódot változtattam. Számomra a Yogivity = vitalitás és erő-egyensúly kívül-belül!`
  },
  {
    name: "Júlia",
    role: "Yogivity résztvevő",
    content: `Imádtam a programot. A részekre osztást, hogy lépésről-lépésre haladtunk. Ha bármi kérdésem volt, arra maximálisan kaptam választ. Mindenképpen ajánlanám jó szívvel. Mert jól összerakott, egymásra épülő program, és ha én végig tudtam csinálni, aki életében soha semmit nem mozgott, akkor más is meg tudja csinálni.`
  },
  {
    name: "László",
    role: "Yogivity résztvevő",
    content: `Ilyen precizitással folytasd, gratulálok az alaposságodhoz, figyelmességedhez, kedvességedhez… összegezve: örülök, hogy részese lehettem a programnak!`
  },
  {
    name: "Andrea",
    role: "Yogivity résztvevő",
    content: `Nagyon jó volt. Vártam az online megbeszéléseket, hogy minél több információhoz jussak. A legjobb a személyes találkozás volt, mert nagyon jók az energiáid és nagyon hiteles, amit képviselsz. Udvarias lenne azt mondani, hogy nem volt semmi negatív dolog, de tényleg ezt kell mondanom, mert sok pozitív dolog történt a 6 hét alatt.`
  },
  {
    name: "Zsófia",
    role: "Yogivity résztvevő",
    content: `Nagyon hasznos volt számomra, hogy átbeszéltük az étrendemet. Rövid idő alatt is sokkal tisztábban láttam, min érdemes változtatnom, mire figyeljek jobban, és hogyan tudok tudatosabban hozzáállni az étkezéshez. Különösen tetszett, hogy nem bonyolult szabályokat kaptam, hanem érthető, gyakorlatias szempontokat, amiket könnyen be tudtam építeni a mindennapokba. Figyelmes, támogató hozzáállást tapasztaltam, ami sokat segített abban, hogy ne teherként, hanem lehetőségként tekintsek a változtatásra.`
  },
  {
    name: "Hajnalka",
    role: "Yogivity résztvevő",
    content: `Az egész program alatt gondoskodást és támogatást éreztem.`
  }
];

export function Testimonials() {
  const trackRef = useRef<HTMLDivElement>(null);

  const moveTestimonial = (dir: number) => {
    const track = trackRef.current;
    if (!track) return;
    const w = track.clientWidth;
    const count = track.children.length;
    const current = Math.round(track.scrollLeft / w);
    const next = (current + dir + count) % count;
    track.scrollTo({ left: next * w, behavior: "smooth" });
  };

  return (
    <section id="testimonials" className="py-24 bg-secondary/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl mb-4">Vélemények</h2>
        </div>

        <div className="max-w-4xl mx-auto flex items-stretch gap-3">
          <button
            type="button"
            aria-label="Előző vélemény"
            onClick={() => moveTestimonial(-1)}
            className="shrink-0 self-center w-11 h-11 rounded-full border border-primary text-primary hidden md:flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div
            ref={trackRef}
            className="flex-1 flex overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {testimonials.map((testimonial, index) => (
              <div key={index} className="shrink-0 w-full snap-center px-1 flex">
                <Card className="bg-white w-full">
                  <CardContent className="p-8">
                    <div className="space-y-6">
                      <blockquote className="text-base leading-relaxed whitespace-pre-line">
                        "{testimonial.content}"
                      </blockquote>

                      <div className="flex items-center space-x-4">
                        {testimonial.image && (
                          <div className="w-16 h-16 rounded-full overflow-hidden">
                            <img
                              src={testimonial.image}
                              alt={testimonial.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div>
                          <div className="text-base">{testimonial.name}</div>
                          <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          <button
            type="button"
            aria-label="Következő vélemény"
            onClick={() => moveTestimonial(1)}
            className="shrink-0 self-center w-11 h-11 rounded-full border border-primary text-primary hidden md:flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
}